import React, { Component } from "react";
import { StoryLine, Character } from "./type";

type Props = {
  sessionId: number;
  riskGroup: number[];
  storylines: StoryLine[];
  characters: Character[];
};

type State = {
  fillColor: string;
  fillOpacity: number;
  strokeColor: string;
  strokeWidth: number;
  rectRadius: number;
};

const colorScheme = [
  {
    risk: "FA",
    policy: "Flase Advertising",
    color: "#fb8072",
  },
  {
    risk: "PP",
    policy: "Protected Products",
    color: "#80b1d3",
  },
  {
    risk: "IB",
    policy: "Inappropriate Business",
    color: "#fdb462",
  },
  {
    risk: "SC",
    policy: "Sensitive content",
    color: "#8dd3c7",
  },
  {
    risk: "NM",
    policy: "Normal",
    color: "#c0c0c0",
  },
];

function pickColor(riskType) {
  for (let i = 0, len = colorScheme.length; i < len; i++) {
    const item = colorScheme[i];
    if (item.risk === riskType) {
      return item.color;
    }
  }
  return "#000";
}

export default class GroupShape extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      fillColor: "#fff",
      fillOpacity: 1,
      strokeColor: "#e78859",
      strokeWidth: 2,
      rectRadius: 5,
    };
  }

  getRectData() {
    const riskGroup = this.props.riskGroup;
    const storylines = this.props.storylines;
    const sessionId = this.props.sessionId;
    for (let i = 0; i < riskGroup.length - 1; i++) {
      for (let j = i + 1; j < riskGroup.length; j++) {
        if (
          storylines[riskGroup[i]][sessionId][0][1] >
          storylines[riskGroup[j]][sessionId][0][1]
        ) {
          const t = riskGroup[i];
          riskGroup[i] = riskGroup[j];
          riskGroup[j] = t;
        }
      }
    }

    const x = storylines[riskGroup[0]][sessionId][1][0];
    const w = storylines[riskGroup[0]][sessionId][2][0] - x;
    const y = storylines[riskGroup[0]][sessionId][1][1];
    const h = storylines[riskGroup[riskGroup.length - 1]][sessionId][2][1] - y;
    const additionY = h;
    const additionY2 = (h + 2 * additionY) / (riskGroup.length - 1) / 2;
    return {
      x: x,
      y: y - additionY - additionY2,
      w: w,
      h: h + 2 * (additionY + additionY2),
    };
  }

  // hard code
  getRectColor() {
    const riskGroup = this.props.riskGroup;
    const characters = this.props.characters;
    const riskTypes = riskGroup.map((cidx) => characters[cidx].split("_")[0]);
    const riskTypesMap = new Map();
    let maxRiskType = "PP";
    let tempCount = -1;
    riskTypes.forEach((riskType) => {
      if (riskTypesMap.has(riskType)) {
        const riskTypeNum = riskTypesMap.get(riskType) + 1;
        if (riskTypeNum > tempCount) {
          tempCount = riskTypeNum;
          maxRiskType = riskType;
        }
        riskTypesMap.set(riskType, riskTypeNum);
      } else {
        riskTypesMap.set(riskType, 1);
      }
    });
    return pickColor(maxRiskType);
  }

  getTextData() {
    const riskGroup = this.props.riskGroup;
    const storylines = this.props.storylines;
    const sessionId = this.props.sessionId;
    const characters = this.props.characters;
    for (let i = 0; i < riskGroup.length - 1; i++) {
      for (let j = i + 1; j < riskGroup.length; j++) {
        if (
          storylines[riskGroup[i]][sessionId][0][1] >
          storylines[riskGroup[j]][sessionId][0][1]
        ) {
          const t = riskGroup[i];
          riskGroup[i] = riskGroup[j];
          riskGroup[j] = t;
        }
      }
    }
    const x =
      (storylines[riskGroup[0]][sessionId][1][0] +
        storylines[riskGroup[0]][sessionId][2][0]) /
      2;
    const y = storylines[riskGroup[0]][sessionId][1][1];
    const h = storylines[riskGroup[riskGroup.length - 1]][sessionId][2][1] - y;
    const additionY = h * 0.8;
    const yStart = y - additionY;
    const yGap = (h + 2 * additionY) / (riskGroup.length - 1);
    return riskGroup.map((cidx, idx) => {
      const [riskType, riskWord] = characters[cidx].split("_");
      return (
        <text
          key={`risk-word-${sessionId}-${characters[cidx]}`}
          x={x}
          y={yStart + idx * yGap}
          style={{
            textAnchor: "middle",
            dominantBaseline: "middle",
            fontSize: riskWord === "Turtle" ? 20 : 14,
            fill: pickColor(riskType),
          }}
        >
          {riskWord}
        </text>
      );
    });
  }

  getCircleData(cx, cy, r) {
    const riskGroup = this.props.riskGroup;
    const characters = this.props.characters;
    const sessionId = this.props.sessionId;
    const riskTypes = riskGroup.map((cidx) => characters[cidx].split("_")[0]);
    const riskTypesMap = new Map();
    riskTypes.forEach((riskType) => {
      if (riskTypesMap.has(riskType)) {
        const riskTypeNum = riskTypesMap.get(riskType) + 1;
        riskTypesMap.set(riskType, riskTypeNum);
      } else {
        riskTypesMap.set(riskType, 1);
      }
    });
    let startAngle = -Math.PI;
    const circleWidth = 1.5;
    return colorScheme.map((c) => {
      if (riskTypesMap.get(c.risk) > 0) {
        const endAngle =
          startAngle +
          (riskTypesMap.get(c.risk) / riskGroup.length) * (2 * Math.PI - 0.001);
        const pathD = generatePieSectorPathD(
          cx,
          cy,
          r - circleWidth * riskGroup.length,
          r + circleWidth * riskGroup.length,
          startAngle,
          endAngle
        );
        startAngle = endAngle;
        return (
          <path
            id={`scene${sessionId}${c.risk}`}
            style={{ fill: c.color, stroke: "none" }}
            d={pathD}
          />
        );
      }
    });
  }

  render() {
    const { fillColor, strokeWidth, fillOpacity, rectRadius } = this.state;
    const strokeColor = this.getRectColor();
    const { x, y, w, h } = this.getRectData();
    const texts = this.getTextData();
    const circles = this.getCircleData(x + w / 2, y + h / 2, Math.min(w, h));
    return (
      <g>
        {/* <rect
          key={`session-${this.props.sessionId}`}
          x={`${x}`}
          y={`${y}`}
          width={`${w}`}
          height={`${h}`}
          style={{
            fill: fillColor,
            fillOpacity: fillOpacity,
            stroke: strokeColor,
            strokeWidth: strokeWidth,
          }}
          rx={rectRadius}
          ry={rectRadius}
        /> */}
        <circle
          cx={x + w / 2}
          cy={y + h / 2}
          r={Math.min(w, h)}
          style={{
            fill: fillColor,
            fillOpacity: fillOpacity,
            stroke: "none",
          }}
        />
        {circles}
        {texts}
      </g>
    );
  }
}

function generatePieSectorPathD(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number
) {
  const six = cx + innerR * Math.cos(startAngle);
  const siy = cy + innerR * Math.sin(startAngle);
  const sox = cx + outerR * Math.cos(startAngle);
  const soy = cy + outerR * Math.sin(startAngle);
  const eox = cx + outerR * Math.cos(endAngle);
  const eoy = cy + outerR * Math.sin(endAngle);
  const eix = cx + innerR * Math.cos(endAngle);
  const eiy = cy + innerR * Math.sin(endAngle);
  if (endAngle - startAngle < Math.PI) {
    return `M ${six} ${siy} L ${sox} ${soy} A ${outerR} ${outerR} 0 0 1 ${eox} ${eoy} L ${eix} ${eiy} A ${innerR} ${innerR} 0 0 0 ${six} ${siy}`;
  } else if (endAngle - startAngle) {
    return `M ${six} ${siy} L ${sox} ${soy} A ${outerR} ${outerR} 0 1 1 ${eox} ${eoy} L ${eix} ${eiy} A ${innerR} ${innerR} 0 1 0 ${six} ${siy}`;
  }
}
